import { isSequenceEqual } from '@dicetechnology/doris-core';
import type { cast as castNs } from 'chromecast-caf-receiver';
import type { MediaStatus } from 'chromecast-caf-receiver/cast.framework.messages';

import { CompatibilityManager, Features } from '../../services';

import { getAudioTracksWithForcedTextTracks } from './getAudioTracksWithForcedTextTracks';
import { getExpectedTrackIdsInMediaStatus } from './getExpectedTrackIdsInMediaStatus';
import { TrackIdTracker } from './TrackIdTracker';

declare const cast: typeof castNs;

export const applySubtitlePolicyPatch = (mediaStatus: MediaStatus): MediaStatus => {
    const patchedMediaStatus = ((mediaStatus: MediaStatus): MediaStatus => {
        if (CompatibilityManager.getInstance().isFeatureSupported(Features.SUBTITLE_POLICY_V2)) {
            return mediaStatus;
        }

        // Get the media from the media status message, or fall back to what the player manager has.
        // This is required because not all status broadcasts will include the media information.
        const media = mediaStatus.media ?? cast.framework.CastReceiverContext.getInstance().getPlayerManager().getMediaInformation();
        if (!media) {
            return mediaStatus;
        }

        // Add `mediaStatus.media` if we need to update the sender with a new list of tracks.
        const trackIdsSeenBySender = TrackIdTracker.getInstance().getTrackIds();
        const audioTracksWithForcedTextTracks = getAudioTracksWithForcedTextTracks(media);
        const expectedTrackIds = getExpectedTrackIdsInMediaStatus(media, mediaStatus.activeTrackIds ?? [], audioTracksWithForcedTextTracks);
        if (!mediaStatus.media && !isSequenceEqual(trackIdsSeenBySender, expectedTrackIds)) {
            mediaStatus.media = media;
        }

        // Check if there is a subtitle policy in effect
        if (audioTracksWithForcedTextTracks.length === 0) {
            return mediaStatus;
        }

        // Remove audioTracksWithForcedTextTracks from customData
        if (mediaStatus.media) {
            const { audioTracksWithForcedTextTracks: _, ...customData } = mediaStatus.media.customData ?? {};
            mediaStatus.media = {
                ...mediaStatus.media,
                customData
            };
        }

        // Filter out subtitles from activeTrackIds and tracks
        if (mediaStatus.activeTrackIds?.some((trackId) => audioTracksWithForcedTextTracks.includes(trackId))) {
            const tracksToRemove = media.tracks?.filter((track) => track.type === cast.framework.messages.TrackType.TEXT) ?? [];

            if (mediaStatus.activeTrackIds) {
                mediaStatus.activeTrackIds = mediaStatus.activeTrackIds.filter(
                    (trackId) => !tracksToRemove.some((track) => track.trackId === trackId)
                );
            }

            if (mediaStatus.media?.tracks) {
                mediaStatus.media = {
                    ...mediaStatus.media,
                    tracks: mediaStatus.media.tracks?.filter(({ trackId }) => !tracksToRemove.some((track) => track.trackId === trackId))
                };
            }
        }

        return mediaStatus;
    })(mediaStatus);

    // Persisting the trackIds that are sent to the sender so we can optionally
    // force include `mediaStatus.media` if we need to update the list of available tracks.
    TrackIdTracker.getInstance().saveTrackIds(patchedMediaStatus);

    return patchedMediaStatus;
};
